// src/pages/Dashboard.js
import { useState, useEffect } from 'react'
import { supabase } from '../utils/supabase' // Assuming you have supabase client setup
import Layout from '../components/common/Layout'
import {
  ChartBarIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  MagnifyingGlassIcon,
  ServerIcon,
  PlusIcon
} from '@heroicons/react/24/outline'
import {handleConnectRepository} from "./Repositories";

function StatCard({ title, value, change, icon: Icon, trend }) {
  const isPositive = trend === 'up'

  return (
      <div className="bg-white rounded-2xl p-6 border border-gray-200 hover:shadow-lg transition-all duration-300">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
            {change && (
                <div className={`flex items-center mt-2 ${
                    isPositive ? 'text-green-600' : 'text-red-600'
                }`}>
                  <span className="text-sm font-medium">{change}</span>
                  <span className="text-xs text-gray-500 ml-1">vs last month</span>
                </div>
            )}
          </div>
          <div className={`p-3 rounded-xl ${
              isPositive ? 'bg-green-100' : 'bg-blue-100'
          }`}>
            <Icon className={`w-6 h-6 ${
                isPositive ? 'text-green-600' : 'text-blue-600'
            }`} />
          </div>
        </div>
      </div>
  )
}

// Toggle Switch Component
function Toggle({ enabled, onChange }) {
  return (
      <button
          type="button"
          className={`${
              enabled ? 'bg-blue-600' : 'bg-gray-200'
          } relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2`}
          role="switch"
          aria-checked={enabled}
          onClick={() => onChange(!enabled)}
      >
      <span
          aria-hidden="true"
          className={`${
              enabled ? 'translate-x-4' : 'translate-x-0'
          } pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
      />
      </button>
  )
}

function ActivityFeed({ activities }) {
  return (
      <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-lg">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Recent Activity</h2>
          <div className="flex items-center text-sm text-gray-500">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-2"></div>
            Live Updates
          </div>
        </div>
        <div className="space-y-4">
          {activities.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircleIcon className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-500 text-lg">No recent activity</p>
                <p className="text-gray-400 text-sm mt-1">Activity will appear here when EchoGuard processes issues</p>
              </div>
          ) : (
              activities.map((activity) => {
                let Icon, color;
                switch (activity.type) {
                  case 'spam_check':
                    Icon = ExclamationTriangleIcon;
                    color = 'red';
                    break;
                  case 'auto_title':
                    Icon = SparklesIcon;
                    color = 'purple';
                    break;
                  case 'similar_issue':
                    Icon = MagnifyingGlassIcon;
                    color = 'green';
                    break;
                  default:
                    Icon = CheckCircleIcon;
                    color = 'blue';
                }

                return (
                    <div key={activity.id} className="flex items-start gap-4 p-4 rounded-xl hover:bg-gray-50 transition-colors duration-200 border border-gray-100">
                      <div className={`p-3 rounded-xl ${
                          color === 'red' ? 'bg-red-100' :
                              color === 'purple' ? 'bg-purple-100' :
                                  color === 'green' ? 'bg-green-100' :
                                      'bg-blue-100'
                      }`}>
                        <Icon className={`w-5 h-5 ${
                            color === 'red' ? 'text-red-600' :
                                color === 'purple' ? 'text-purple-600' :
                                    color === 'green' ? 'text-green-600' :
                                        'text-blue-600'
                        }`} />
                      </div>
                      <div className="flex-1">
                        <p className="text-base font-medium text-gray-900">{activity.log_message}</p>
                        <p className="text-sm text-gray-500 mt-1">
                          {new Date(activity.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                )
              })
          )}
        </div>
      </div>
  )
}

function RepositorySummary({ repositories, onToggleRepo, onAddRepo }) {
  const activeRepos = repositories.filter(repo => repo.is_active).length
  const totalRepos = repositories.length

  return (
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Repository Usage</h3>
          <ServerIcon className="w-5 h-5 text-gray-400" />
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Active Repositories</span>
            <span className="text-lg font-bold text-green-600">{activeRepos}</span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Total Connected</span>
            <span className="text-lg font-bold text-gray-900">{totalRepos}</span>
          </div>

          {repositories.length > 0 && (
              <>
                <div className="border-t border-gray-200 pt-4">
                  <p className="text-xs text-gray-500 mb-3">Repositories</p>
                  <div className="space-y-2">
                    {repositories.map((repo) => (
                        <div key={repo.id} className="flex items-center justify-between p-2 rounded-lg bg-gray-50">
                          <div className="flex items-center gap-2">
                            <div
                                className={`w-2 h-2 mt-0.5 rounded-full ${repo.is_active ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                            <span className="text-sm font-medium text-gray-700 truncate max-w-[120px]">
                              {repo.name.split('/').pop()}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500">
                            {repo.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                    ))}
                  </div>
                </div>
              </>
          )}

          {/* Add New Repository Button */}
          <div className="border-t border-gray-200 pt-4">
            <button
                onClick={onAddRepo}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors duration-200 font-medium"
            >
              <PlusIcon className="w-5 h-5"/>
              Add Repository
            </button>
          </div>
        </div>
      </div>
  )
}

export default function Dashboard() {
  const [timeRange, setTimeRange] = useState('7d')
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState([])
  const [repositories, setRepositories] = useState([])
  const [activities, setActivities] = useState([])

  // Handle repository toggle
  const handleToggleRepo = async (repoId, isActive) => {
    try {
      const { error } = await supabase
          .from('repositories')
          .update({ is_active: isActive })
          .eq('id', repoId)

      if (error) throw error

      // Update local state
      setRepositories(prev =>
          prev.map(repo =>
              repo.id === repoId ? { ...repo, is_active: isActive } : repo
          )
      )
    } catch (error) {
      console.error('Error toggling repository:', error)
    }
  }


  // Calculate date range based on selection
  const getDateRange = (range) => {
    const now = new Date()
    const days = range === '7d' ? 7 : range === '30d' ? 30 : 90
    const startDate = new Date(now.getTime() - (days * 24 * 60 * 60 * 1000))
    return startDate.toISOString()
  }

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      const startDate = getDateRange(timeRange)

      // Get user's repositories
      const { data: reposData, error: reposError } = await supabase
          .from('repositories')
          .select('*')
          .eq('is_active', true)

      if (reposError) throw reposError

// Get activity logs for the time range
      const { data: logsData, error: logsError } = await supabase
          .from('activity_logs')
          .select('*')
          .gte('created_at', startDate)
          .order('created_at', { ascending: false })

      if (logsError) throw logsError

// Get similar issue links for the time range
      const { data: similarLinksData, error: similarLinksError } = await supabase
          .from('similar_issue_links')
          .select('*')
          .gte('created_at', startDate)

      if (similarLinksError) throw similarLinksError

// Get previous period data for comparison
      const prevStartDate = new Date(new Date(startDate).getTime() - (getDateRange(timeRange) ? 30 : 7) * 24 * 60 * 60 * 1000).toISOString()

      const { data: prevLogsData, error: prevLogsError } = await supabase
          .from('activity_logs')
          .select('*')
          .gte('created_at', prevStartDate)
          .lt('created_at', startDate)

      if (prevLogsError) throw prevLogsError

// Get previous period similar issue links
      const { data: prevSimilarLinksData, error: prevSimilarLinksError } = await supabase
          .from('similar_issue_links')
          .select('*')
          .gte('created_at', prevStartDate)
          .lt('created_at', startDate)

      if (prevSimilarLinksError) throw prevSimilarLinksError

// Calculate current period stats
      const currentStats = calculateStats(logsData, similarLinksData)
      const previousStats = calculateStats(prevLogsData, prevSimilarLinksData)

      // Calculate percentage changes
      const statsWithChanges = [
        {
          title: 'Issues Processed',
          value: currentStats.totalIssues.toLocaleString(),
          change: calculateChange(currentStats.totalIssues, previousStats.totalIssues),
          icon: ChartBarIcon,
          trend: currentStats.totalIssues >= previousStats.totalIssues ? 'up' : 'down'
        },
        {
          title: 'Similar Issues Detected',
          value: currentStats.similarIssues.toLocaleString(),
          change: calculateChange(currentStats.similarIssues, previousStats.similarIssues),
          icon: MagnifyingGlassIcon,
          trend: currentStats.similarIssues >= previousStats.similarIssues ? 'up' : 'down'
        },
        {
          title: 'New Titles Generated',
          value: currentStats.autoTitles.toLocaleString(),
          change: calculateChange(currentStats.autoTitles, previousStats.autoTitles),
          icon: SparklesIcon,
          trend: currentStats.autoTitles >= previousStats.autoTitles ? 'up' : 'down'
        },
        {
          title: 'Estimated Time Saved',
          value: `${currentStats.timeSaved}h`,
          change: calculateChange(currentStats.timeSaved, previousStats.timeSaved, 'h'),
          icon: ClockIcon,
          trend: currentStats.timeSaved >= previousStats.timeSaved ? 'up' : 'down'
        }
      ]

      setRepositories(reposData || [])
      setStats(statsWithChanges)
      setActivities(logsData?.slice(0, 15) || []) // Show more activities since it's the main focus

    } catch (error) {
      console.error('Error fetching dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

// Calculate stats from activity logs and similar issue links
  const calculateStats = (logs, similarIssueLinks) => {
    const uniqueIssueIds = new Set(logs.map(log => log.issue_id))
    const totalIssues = uniqueIssueIds.size
    const spamDetected = logs.filter(log => log.type === 'spam_check' && log.has_change).length
    const autoTitles = logs.filter(log => log.type === 'auto_title' && log.has_change).length

    // Similar issues count from similar_issue_links table
    const similarIssues = similarIssueLinks.length

    // Calculate estimated time saved: similar_issues * 10 + auto_titles * 5 + spam * 10 (in minutes, then convert to hours)
    const timeSavedMinutes = (similarIssues * 10) + (autoTitles * 5) + (spamDetected * 10)
    const timeSaved = Math.round(timeSavedMinutes / 60 * 10) / 10 // Round to 1 decimal place

    return {
      totalIssues,
      spamDetected,
      similarIssues,
      autoTitles,
      timeSaved
    }
  }

  // Calculate percentage change
  const calculateChange = (current, previous, suffix = '') => {
    if (previous === 0) {
      return current > 0 ? `+${current}${suffix}` : `${current}${suffix}`
    }
    const change = ((current - previous) / previous * 100).toFixed(0)
    return `${change >= 0 ? '+' : ''}${change}%`
  }

  useEffect(() => {
    fetchDashboardData()
  }, [timeRange])

  if (loading) {
    return (
        <Layout>
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </Layout>
    )
  }

  return (
      <Layout>
        <div className="space-y-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
              <p className="mt-2 text-gray-600">
                Monitor your repository automation and see how much time EchoGuard is saving you
              </p>
            </div>
            <div className="mt-4 sm:mt-0">
              <select
                  value={timeRange}
                  onChange={(e) => setTimeRange(e.target.value)}
                  className="px-4 py-2 pr-8 rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white min-w-[140px]"
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
              </select>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
                <StatCard key={index} {...stat} />
            ))}
          </div>

          {/* Main Content - Activity Feed as Primary */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Recent Activity - Main Focus */}
            <div className="lg:col-span-3">
              <ActivityFeed activities={activities} />
            </div>

            {/* Repository Summary - Secondary */}
            <div className="lg:col-span-1">
              <RepositorySummary
                  repositories={repositories}
                  onToggleRepo={handleToggleRepo}
                  onAddRepo={handleConnectRepository}
              />
            </div>
          </div>
        </div>
      </Layout>
  )
}
